const API_URL = import.meta.env.VITE_API_URL || '';

export const getToken = (): string | null => localStorage.getItem('dk_token');
export const setToken = (t: string) => localStorage.setItem('dk_token', t);
export const clearToken = () => {
  // Phase 31 BUG 1 — temporary diagnostic: log WHO cleared the token so a
  // logout-on-refresh can be traced to its exact caller in DevTools.
  try { console.debug('[auth] clearToken()', new Error().stack?.split('\n').slice(2, 4).join(' <- ')); } catch {}
  localStorage.removeItem('dk_token');
  localStorage.removeItem('dk_user');
  // Phase 11: reset silent-refresh budget on sign-out so the next user
  // session starts with a fresh cooldown / burst counter.
  try { resetSilentRefresh(); } catch {}
};

// Phase 9: a single subscriber slot for "auth expired" so the UI layer
// (a banner / hook) can react without the api client knowing about React.
type AuthListener = (reason: string) => void;
let _authListener: AuthListener | null = null;
export const setAuthExpiredListener = (l: AuthListener | null) => { _authListener = l; };

// Phase 9: track ongoing silent-refresh so multiple parallel requests
// don't all hammer /refresh in the same instant.
//
// Phase 11 P0: bound the failure cascade. If the refresh endpoint keeps
// failing (network blip, server degraded, cookie missing), don't pile
// new attempts every time another request 401s. We use:
//   - REFRESH_FAILURE_COOLDOWN_MS — after a failed attempt, refuse to
//     retry for this window. The 401 will surface to the user via the
//     auth-expired banner once.
//   - REFRESH_MAX_BURST — hard ceiling on attempts per session.
let _refreshInflight: Promise<string | null> | null = null;
let _refreshFailedUntil = 0;
let _refreshAttemptCount = 0;
const REFRESH_FAILURE_COOLDOWN_MS = 30_000;
const REFRESH_MAX_BURST = 5;

async function _trySilentRefresh(): Promise<string | null> {
  // If we recently failed, do nothing — surface the 401 to the user.
  if (Date.now() < _refreshFailedUntil) return null;
  if (_refreshAttemptCount >= REFRESH_MAX_BURST) return null;
  if (_refreshInflight) return _refreshInflight;
  _refreshAttemptCount++;
  _refreshInflight = (async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',     // sends ss_refresh cookie
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!res.ok) {
        _refreshFailedUntil = Date.now() + REFRESH_FAILURE_COOLDOWN_MS;
        return null;
      }
      const data = await res.json();
      if (data.accessToken) {
        setToken(data.accessToken);
        // A successful refresh resets the burst counter — fresh slate.
        _refreshAttemptCount = 0;
        return data.accessToken as string;
      }
      _refreshFailedUntil = Date.now() + REFRESH_FAILURE_COOLDOWN_MS;
      return null;
    } catch {
      _refreshFailedUntil = Date.now() + REFRESH_FAILURE_COOLDOWN_MS;
      return null;
    } finally {
      _refreshInflight = null;
    }
  })();
  return _refreshInflight;
}

// Test/UI helper — reset state when the user signs out so the next
// session starts with a fresh budget.
export const resetSilentRefresh = () => {
  _refreshInflight = null;
  _refreshFailedUntil = 0;
  _refreshAttemptCount = 0;
};

export async function apiFetch(path: string, options: RequestInit = {}, retried = false): Promise<any> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_URL}${path}`, { ...options, headers, credentials: options.credentials ?? 'include' });

  // Phase 9: 401 → try one silent refresh, then retry; if it still fails,
  // surface the auth-expired event so the UI can react gracefully.
  // Only treat a 401 as an *expired session* when the user actually had a token.
  // On public pages (register, login, forgot-password) there is no session, so a
  // 401 is just "unauthenticated" — never show the "session expired" banner there.
  if (res.status === 401 && token && !retried && !path.startsWith('/api/auth/refresh') && !path.startsWith('/api/auth/login')) {
    const fresh = await _trySilentRefresh();
    if (fresh) return apiFetch(path, options, true);
    if (_authListener) try { _authListener('expired'); } catch {}
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Phase 20.1 — attach the HTTP status to the error. useAuth (and any
    // other 401/403-aware caller) was previously regex-matching the
    // error message for `HTTP 401`, but the server's JSON `{error}`
    // body takes precedence over the synthesized "HTTP NNN" string, so
    // an auth failure with a populated error body like `"unauthorized"`
    // never triggered the stale-token cleanup. Exposing `.status`
    // directly makes the check unambiguous.
    const err: any = new Error(data.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export const authApi = {
  // Phase 10: pass `{ withRefresh: true }` to get the access+refresh pair.
  login: (email: string, password: string, opts: { withRefresh?: boolean } = {}) =>
    apiFetch(
      `/api/auth/login${opts.withRefresh ? '?withRefresh=1' : ''}`,
      { method: 'POST', body: JSON.stringify({ email, password }) }
    ),
  register: (email: string, password: string, displayName?: string, companyName?: string) =>
    apiFetch('/api/auth/register', { method: 'POST', body: JSON.stringify({ email, password, displayName, companyName }) }),
  me: () => apiFetch('/api/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    apiFetch('/api/auth/change-password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),
};

export const devicesApi = {
  list: () => apiFetch('/api/devices'),
  get: (id: string) => apiFetch(`/api/devices/${id}`),
  update: (id: string, data: any) => apiFetch(`/api/devices/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => apiFetch(`/api/devices/${id}`, { method: 'DELETE' }),
  // Phase 16 — admin re-enroll. Returns { enrollToken, oneLiner, … } for
  // copy-paste install on the target Windows host.
  reEnroll: (id: string): Promise<{
    deviceId: string; hostname: string; enrollToken: string;
    expiresAt: string; serverUrl: string; oneLiner: string;
    instructions: string[];
  }> => apiFetch(`/api/devices/${id}/re-enroll`, { method: 'POST' }),
};

// Phase 16 — agent manifest (unauthenticated; used by installers).
export const agentApi = {
  manifest: (): Promise<{
    latestVersion: string; publishedAt: string;
    downloadUrl: string; installerUrl: string;
    sha256: string | null; notes: string[];
  }> => fetch(`${API_URL}/api/agent/update/manifest`).then((r) => r.json()),
};

export const screenshotsApi = {
  list: (params: { deviceId?: string; from?: string; to?: string; limit?: number } = {}) => {
    const q = new URLSearchParams(params as any).toString();
    return apiFetch(`/api/screenshots${q ? '?' + q : ''}`);
  },
};

export const sessionsApi = {
  list: (params: { deviceId?: string; limit?: number } = {}) => {
    const q = new URLSearchParams(params as any).toString();
    return apiFetch(`/api/sessions${q ? '?' + q : ''}`);
  },
};

export const telemetryApi = {
  list: (params: { deviceId?: string; eventType?: string; limit?: number } = {}) => {
    const q = new URLSearchParams(params as any).toString();
    return apiFetch(`/api/telemetry${q ? '?' + q : ''}`);
  },
};

export interface EnrollmentFailureRow {
  id: string;
  occurredAt: string;
  hostname: string | null;
  ip: string | null;
  reason: 'token_not_found' | 'token_expired' | 'token_consumed' | 'token_revoked' | 'missing_token' | string;
  tokenHash: string | null;
  orgId: string | null;
}

export const enrollmentApi = {
  list: () => apiFetch('/api/enrollment'),
  create: (label?: string, expiresInHours?: number) =>
    apiFetch('/api/enrollment', { method: 'POST', body: JSON.stringify({ label, expiresInHours }) }),
  delete: (id: string) => apiFetch(`/api/enrollment/${id}`, { method: 'DELETE' }),
  // Phase 22.1 — feed for the "Recent Enrollment Failures" widget.
  failures: (sinceHours = 24, limit = 20): Promise<EnrollmentFailureRow[]> =>
    apiFetch(`/api/enrollment/failures?sinceHours=${sinceHours}&limit=${limit}`),
};

export const usersApi = {
  list: () => apiFetch('/api/users'),
  create: (data: any) => apiFetch('/api/users', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => apiFetch(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => apiFetch(`/api/users/${id}`, { method: 'DELETE' }),
};

export const orgsApi = {
  list: () => apiFetch('/api/organizations'),
  update: (id: string, data: any) => apiFetch(`/api/organizations/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
};

// ─── Phase 35 — Multi-tenant SaaS (billing, usage, invites) ──────────────────
export interface BillingUsage {
  devices: number; users: number; limit: number; unlimited: boolean;
  available: number | null; pct: number; nearLimit: boolean; atLimit: boolean; planKey: string;
}
export interface BillingTrial {
  active: boolean; endsAt: string | null; daysLeft: number | null; expired: boolean;
}
export interface BillingInvoice {
  id: string; date: string | null; description: string; amount: number;
  currency: string; provider: string; status: string; reference: string | null;
}
export interface BillingPlanCatalog {
  key: string; name: string; pricePerUser: number; maxUsers: number;
  retention: number; features: string[]; current: boolean;
}
export interface BillingOverview {
  organization: { id: string; name: string; slug: string | null; logo: string; timezone: string; industry: string; createdAt: string };
  plan: { key: string; name: string; pricePerUser: number; maxUsers: number; retention: number; features: string[] };
  usage: BillingUsage;
  trial: BillingTrial;
  billingStatus: string;
  degraded: boolean;
  billingCycle: string;
  estimate: { currency: string; perUser: number; seats: number; subtotal: number; gst: number; total: number };
  invoices: BillingInvoice[];
  catalog: BillingPlanCatalog[];
}

export const billingApi = {
  overview: (): Promise<BillingOverview> => apiFetch('/api/billing'),
  usage: (): Promise<BillingUsage> => apiFetch('/api/billing/usage'),
  updateOrganization: (patch: { name?: string; logo?: string; timezone?: string; industry?: string; slug?: string }) =>
    apiFetch('/api/billing/organization', { method: 'PATCH', body: JSON.stringify(patch) }),
  cancel: (reason?: string) => apiFetch('/api/billing/cancel', { method: 'POST', body: JSON.stringify({ reason }) }),
};

// ─── Phase 2 — Admin Center (product licensing) ──────────────────────────────
export type LicenseStatus = 'trial' | 'active' | 'suspended' | 'cancelled' | 'expired';
export type BillingCycle = 'trial' | 'monthly' | 'quarterly' | 'yearly' | 'lifetime' | 'enterprise';
export type SeatModel = 'per_user' | 'per_org' | 'per_workspace' | 'concurrent' | 'named' | 'unlimited';
export interface ProductLicense {
  _id: string; orgId: string; product: string; plan: string;
  status: LicenseStatus; billingCycle: BillingCycle; seatModel: SeatModel;
  seatsTotal: number | null; seatsUsed: number;
  limits: { storageGb: number | null; aiCredits: number | null; apiCallsPerMonth: number | null; devices: number | null };
  usage: Record<string, number | null>;
  startedAt: string; trialEndsAt: string | null; expiresAt: string | null;
  source: string; active: boolean;
}
export interface AdminProduct {
  key: string; name: string; shortName: string; description: string;
  url: string; category: string; icon: string; color: string; status: string; base: boolean;
  license: ProductLicense | null;
}
export interface AdminOverview {
  organization: { id: string; name: string; slug: string | null } | null;
  plan: string; billingStatus: string;
  counts: { products: number; licensed: number; active: number };
  products: AdminProduct[];
}
export interface AssignLicenseBody {
  product: string; plan?: string; billingCycle?: BillingCycle;
  seatsTotal?: number | null; seatModel?: SeatModel; status?: LicenseStatus;
}

export const adminApi = {
  overview: (): Promise<AdminOverview> => apiFetch('/api/admin/overview'),
  licenses: (): Promise<{ licenses: ProductLicense[] }> => apiFetch('/api/admin/licenses'),
  assign: (body: AssignLicenseBody): Promise<{ license: ProductLicense }> =>
    apiFetch('/api/admin/licenses', { method: 'POST', body: JSON.stringify(body) }),
  bulkAssign: (products: string[], plan?: string, billingCycle?: BillingCycle): Promise<{ assigned: string[] }> =>
    apiFetch('/api/admin/licenses/bulk', { method: 'POST', body: JSON.stringify({ products, plan, billingCycle }) }),
  updateLicense: (product: string, patch: { plan?: string; seatsTotal?: number | null; seatModel?: SeatModel; billingCycle?: BillingCycle; limits?: Record<string, number | null> }): Promise<{ license: ProductLicense }> =>
    apiFetch(`/api/admin/licenses/${product}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  suspend: (product: string, reason?: string) =>
    apiFetch(`/api/admin/licenses/${product}/suspend`, { method: 'POST', body: JSON.stringify({ reason }) }),
  resume: (product: string) => apiFetch(`/api/admin/licenses/${product}/resume`, { method: 'POST' }),
  cancel: (product: string) => apiFetch(`/api/admin/licenses/${product}/cancel`, { method: 'POST' }),
  renew: (product: string, billingCycle?: BillingCycle) =>
    apiFetch(`/api/admin/licenses/${product}/renew`, { method: 'POST', body: JSON.stringify({ billingCycle }) }),
  remove: (product: string) => apiFetch(`/api/admin/licenses/${product}`, { method: 'DELETE' }),

  // Notification platform — delivery observability + retry + test.
  deliveries: (params: { channel?: string; status?: string; limit?: number; skip?: number } = {}): Promise<{ total: number; deliveries: NotificationDelivery[] }> => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== "") q.set(k, String(v)); });
    return apiFetch(`/api/admin/notifications/deliveries${q.toString() ? "?" + q : ""}`);
  },
  deliveryStats: (): Promise<{ byChannel: Record<string, Record<string, number>>; byStatus: Record<string, number> }> =>
    apiFetch('/api/admin/notifications/stats'),
  retryDelivery: (id: string) => apiFetch(`/api/admin/notifications/deliveries/${id}/retry`, { method: 'POST' }),
  retryFailedDeliveries: (): Promise<{ retried: number; recovered: number }> =>
    apiFetch('/api/admin/notifications/deliveries/retry-failed', { method: 'POST' }),
  testNotification: (title?: string) =>
    apiFetch('/api/admin/notifications/test', { method: 'POST', body: JSON.stringify({ title }) }),

  // Developer platform — API keys.
  apiKeys: (): Promise<{ keys: ApiKeyRow[]; scopes: ApiScope[] }> => apiFetch('/api/admin/api-keys'),
  createApiKey: (name: string, scopes: string[], expiresInDays?: number): Promise<{ key: string; apiKey: ApiKeyRow }> =>
    apiFetch('/api/admin/api-keys', { method: 'POST', body: JSON.stringify({ name, scopes, expiresInDays }) }),
  revokeApiKey: (id: string) => apiFetch(`/api/admin/api-keys/${id}`, { method: 'DELETE' }),
};

export interface ApiScope { key: string; description: string }
export interface ApiKeyRow {
  id: string; name: string; prefix: string; scopes: string[];
  lastUsedAt: string | null; expiresAt: string | null; revokedAt: string | null;
  createdAt: string; active: boolean;
}

export interface NotificationDelivery {
  id: string; channel: string; target: string; event: string; severity: string;
  title: string; status: 'sent' | 'failed' | 'skipped' | 'pending'; attempts: number;
  error: string; sentAt: string | null; createdAt: string; notificationId: string | null;
}

// ─── Phase 2 — Feature flags ─────────────────────────────────────────────────
export interface AdminFlag {
  name: string; description: string; owner: string; since: string | null;
  default: unknown; effective: unknown; override: unknown; hasOverride: boolean;
}
// ─── Phase 1/2 — Product catalog (marketplace + launcher) ────────────────────
export interface CatalogProduct {
  key: string; name: string; shortName: string; description: string;
  url: string; category: string; icon: string; color: string;
  status: string; base: boolean; entitled?: boolean;
}
export const productsApi = {
  catalog: (): Promise<{ products: CatalogProduct[] }> => apiFetch('/api/products/catalog'),
  mine: (): Promise<{ entitled: string[]; products: CatalogProduct[] }> => apiFetch('/api/products/mine'),
};

export const flagsApi = {
  // Evaluated flags for the current principal (frontend runtime gating).
  evaluated: (): Promise<{ flags: Record<string, unknown> }> => apiFetch('/api/flags'),
  // Admin: catalog + per-org overrides.
  adminList: (): Promise<{ flags: AdminFlag[] }> => apiFetch('/api/admin/flags'),
  set: (flag: string, value: unknown) =>
    apiFetch(`/api/admin/flags/${flag}`, { method: 'PUT', body: JSON.stringify({ value }) }),
  clear: (flag: string) => apiFetch(`/api/admin/flags/${flag}`, { method: 'DELETE' }),
};

export type InviteRole = 'admin' | 'manager' | 'viewer' | 'employee';
export interface InviteRow {
  id: string; email: string; role: InviteRole; status: 'pending' | 'accepted' | 'revoked';
  orgName: string | null; invitedByName: string; expiresAt: string; createdAt: string;
  acceptedAt: string | null; expired: boolean;
  acceptUrl?: string; token?: string;
}

// ─── Phase 36 — Integrations Hub ─────────────────────────────────────────────
export type IntegrationType = 'slack' | 'teams' | 'google' | 'jira' | 'zoho-cliq';
export type IntegrationCategory = 'Communication' | 'Project Management' | 'HR' | 'CRM' | 'Other';

export interface IntegrationField {
  key: string;
  label: string;
  type: 'text' | 'url' | 'password' | 'textarea';
  placeholder?: string;
  required?: boolean;
  secret?: boolean;
}
export interface IntegrationCatalogEntry {
  type: IntegrationType;
  name: string;
  category: IntegrationCategory;
  description: string;
  supportsCommands: boolean;
  fields: IntegrationField[];
}
export interface IntegrationEvents { alerts: boolean; digest: boolean; attendance: boolean; }
export interface IntegrationRow {
  type: IntegrationType;
  name: string;
  category: IntegrationCategory;
  status: 'connected' | 'disconnected' | 'error';
  meta: Record<string, any>;
  events: IntegrationEvents;
  supportsCommands: boolean;
  inboundUrl: string | null;
  lastSync: string | null;
  lastTestAt: string | null;
  lastError: string;
  connectedAt: string;
}
export interface JiraIssue {
  key: string; summary: string; status: string; priority: string; type: string; updated: string | null; url: string;
}

export const integrationsApi = {
  list: (): Promise<IntegrationRow[]> => apiFetch('/api/integrations'),
  catalog: (): Promise<IntegrationCatalogEntry[]> => apiFetch('/api/integrations/catalog'),
  connect: (type: IntegrationType, config: Record<string, any>, events?: Partial<IntegrationEvents>): Promise<IntegrationRow> =>
    apiFetch(`/api/integrations/${type}/connect`, { method: 'POST', body: JSON.stringify({ config, events }) }),
  updateEvents: (type: IntegrationType, events: Partial<IntegrationEvents>): Promise<IntegrationRow> =>
    apiFetch(`/api/integrations/${type}/events`, { method: 'PATCH', body: JSON.stringify({ events }) }),
  test: (type: IntegrationType): Promise<{ ok: boolean; message: string }> =>
    apiFetch(`/api/integrations/${type}/test`, { method: 'POST' }),
  disconnect: (type: IntegrationType): Promise<{ ok: boolean }> =>
    apiFetch(`/api/integrations/${type}`, { method: 'DELETE' }),
  jiraIssues: (assignee?: string): Promise<{ issues: JiraIssue[]; total: number }> =>
    apiFetch(`/api/integrations/jira/issues${assignee ? `?assignee=${encodeURIComponent(assignee)}` : ''}`),
  googleSync: (): Promise<{ ok: boolean; reason?: string; message?: string }> =>
    apiFetch('/api/integrations/google/sync', { method: 'POST' }),
  // Prodify360 ecosystem cross-tool connections (timewatch / assets / helpdesk).
  getEcosystem: (): Promise<Record<string, boolean>> => apiFetch('/api/integrations/ecosystem'),
  updateEcosystem: (patch: Record<string, boolean>): Promise<Record<string, boolean>> =>
    apiFetch('/api/integrations/ecosystem', { method: 'PATCH', body: JSON.stringify(patch) }),
};

export const invitesApi = {
  list: (): Promise<InviteRow[]> => apiFetch('/api/invites'),
  create: (email: string, role: InviteRole, expiryDays?: number): Promise<InviteRow> =>
    apiFetch('/api/invites', { method: 'POST', body: JSON.stringify({ email, role, expiryDays }) }),
  revoke: (id: string) => apiFetch(`/api/invites/${id}`, { method: 'DELETE' }),
  // Public (no auth) — used by the /invite/:token accept page.
  preview: (token: string): Promise<InviteRow> => apiFetch(`/api/invites/token/${encodeURIComponent(token)}`),
  accept: (token: string, password: string, displayName?: string): Promise<{ token: string; user: any }> =>
    apiFetch(`/api/invites/token/${encodeURIComponent(token)}/accept`, { method: 'POST', body: JSON.stringify({ password, displayName }) }),
};

export const dashboardApi = { get: () => apiFetch('/api/dashboard') };

// Phase 27 — Attendance & Shift Management.
export interface AttendanceRow {
  id: string;
  deviceId: string;
  userId: string | null;
  hostname: string;
  employeeName: string;
  department: string;
  date: string;
  clockIn: string | null;
  clockOut: string | null;
  totalMinutes: number;
  totalHours: string;
  activeMinutes: number;
  overtimeMinutes: number;
  status: 'present' | 'late' | 'absent' | 'half_day' | 'leave' | 'holiday';
  shiftId: string | null;
  leaveType: string;
  note: string;
  override: boolean;
}
export interface AttendanceSummary {
  from: string;
  to: string;
  count: number;
  summary: { present: number; late: number; absent: number; half_day: number; leave: number; holiday: number; overtimeMinutes: number; totalMinutes: number };
}
export interface ShiftRow {
  id: string;
  _id: string;
  name: string;
  startTime: string;
  endTime: string;
  graceMinutes: number;
  isDefault: boolean;
  active: boolean;
  assignedDevices: number;
}

function attQuery(params: Record<string, string | undefined>) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
  const s = q.toString();
  return s ? '?' + s : '';
}

export const attendanceApi = {
  list: (params: { from?: string; to?: string; deviceId?: string; userId?: string; status?: string; department?: string } = {}): Promise<{ from: string; to: string; records: AttendanceRow[] }> =>
    apiFetch(`/api/attendance${attQuery(params)}`),
  summary: (params: { from?: string; to?: string } = {}): Promise<AttendanceSummary> =>
    apiFetch(`/api/attendance/summary${attQuery(params)}`),
  // Export streams CSV (not JSON) — fetch with the auth header and hand
  // back a Blob the caller can download.
  exportUrl: (params: { from?: string; to?: string; department?: string; status?: string } = {}) =>
    `${API_URL}/api/attendance/export${attQuery(params)}`,
  exportCsv: async (params: { from?: string; to?: string; department?: string; status?: string } = {}): Promise<Blob> => {
    const res = await fetch(attendanceApi.exportUrl(params), {
      headers: { Authorization: `Bearer ${getToken()}` },
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`);
    return res.blob();
  },
  update: (id: string, patch: { clockIn?: string | null; clockOut?: string | null; status?: string; leaveType?: string; note?: string }) =>
    apiFetch(`/api/attendance/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
};

// Phase 28 — Reports hub data sources (existing endpoints, no new backend).
function rptQuery(from?: string, to?: string) {
  const q = new URLSearchParams();
  if (from) q.set('from', from);
  if (to) q.set('to', to);
  const s = q.toString();
  return s ? '?' + s : '';
}
export const reportsApi = {
  // Per-employee online/active/idle/break + keys/clicks over a range.
  activityDetailed: (from?: string, to?: string): Promise<{ report: any[] }> =>
    apiFetch(`/api/reports/activity-detailed${rptQuery(from, to)}`),
  // Per-employee app/URL usage rows.
  appsUrls: (from?: string, to?: string): Promise<{ report: any[]; total: number }> =>
    apiFetch(`/api/reports/apps-urls${rptQuery(from, to)}`),
};
// Org-wide app/url/category aggregation with productivity classification.
export const appsUrlsApi = {
  summary: (from?: string, to?: string): Promise<{
    apps: any[]; urls: any[]; categories: any[];
    totalMs: number; productiveMs: number; unproductiveMs: number; neutralMs: number;
    totalScreenshots: number; totalDevices: number;
  }> => apiFetch(`/api/apps-urls/summary${rptQuery(from, to)}`),
};

// Phase 30 — Team Management.
export interface TeamMemberLite { id: string; name: string; role: 'manager' | 'member'; designation?: string; avatar?: string; }
export interface TeamCard { id: string; name: string; description: string; color: string; departmentId: string | null; manager: { id: string; name: string; role: string } | null; memberCount: number; members: TeamMemberLite[]; }
export interface DepartmentNode { id: string; name: string; description: string; color: string; head: { id: string; name: string } | null; teams: TeamCard[]; teamCount: number; memberCount: number; }
export interface TeamsOverview { scope: 'org' | 'manager' | 'member'; departments: DepartmentNode[]; orphanTeams: TeamCard[]; totals: { departments: number; teams: number; members: number }; }
export interface TeamComparison { from: string; to: string; orgAvg: number; teams: Array<{ id: string; name: string; color: string; memberCount: number; withData: number; avgProductivity: number; activeHours: number; manager: string | null }>; }
export interface TeamBenchmark { from: string; to: string; team: { id: string; name: string }; teamAvg: number; orgAvg: number; members: Array<{ id: string; name: string; designation: string; role: string; productivity: number; activeHours: number; hasData: boolean }>; }

export const teamsApi = {
  overview: (): Promise<TeamsOverview> => apiFetch('/api/teams/overview'),
  comparison: (from?: string, to?: string): Promise<TeamComparison> => apiFetch(`/api/teams/comparison${from || to ? `?from=${from || ''}&to=${to || ''}` : ''}`),
  benchmark: (teamId: string, from?: string, to?: string): Promise<TeamBenchmark> => apiFetch(`/api/teams/benchmark?teamId=${teamId}${from ? '&from=' + from : ''}${to ? '&to=' + to : ''}`),
  createDepartment: (body: { name: string; description?: string; color?: string }) => apiFetch('/api/teams/departments', { method: 'POST', body: JSON.stringify(body) }),
  updateDepartment: (id: string, patch: any) => apiFetch(`/api/teams/departments/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteDepartment: (id: string) => apiFetch(`/api/teams/departments/${id}`, { method: 'DELETE' }),
  createTeam: (body: { name: string; departmentId?: string | null; description?: string; color?: string; managerId?: string | null }) => apiFetch('/api/teams', { method: 'POST', body: JSON.stringify(body) }),
  updateTeam: (id: string, patch: any) => apiFetch(`/api/teams/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteTeam: (id: string) => apiFetch(`/api/teams/${id}`, { method: 'DELETE' }),
  addMember: (teamId: string, userId: string, role?: 'manager' | 'member') => apiFetch(`/api/teams/${teamId}/members`, { method: 'POST', body: JSON.stringify({ userId, role }) }),
  removeMember: (teamId: string, userId: string) => apiFetch(`/api/teams/${teamId}/members/${userId}`, { method: 'DELETE' }),
  setManager: (teamId: string, userId: string | null) => apiFetch(`/api/teams/${teamId}/manager`, { method: 'PATCH', body: JSON.stringify({ userId }) }),
};

export const shiftsApi = {
  list: (): Promise<{ shifts: ShiftRow[] }> => apiFetch('/api/shifts'),
  create: (body: { name: string; startTime: string; endTime: string; graceMinutes?: number; isDefault?: boolean }) =>
    apiFetch('/api/shifts', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, patch: Partial<{ name: string; startTime: string; endTime: string; graceMinutes: number; isDefault: boolean; active: boolean }>) =>
    apiFetch(`/api/shifts/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  remove: (id: string) => apiFetch(`/api/shifts/${id}`, { method: 'DELETE' }),
  assign: (id: string, deviceIds: string[]) =>
    apiFetch(`/api/shifts/${id}/assign`, { method: 'POST', body: JSON.stringify({ deviceIds }) }),
};

export const licensesApi = {
  list: () => apiFetch('/api/licenses'),
  update: (orgId: string, data: any) => apiFetch(`/api/licenses/${orgId}`, { method: 'PATCH', body: JSON.stringify(data) }),
};

export const auditApi = { list: () => apiFetch('/api/audit-logs') };

// Phase 22.4 — Update Center.
export interface AgentUpdateRow {
  id: string;
  hostname: string;
  agentVersion: string | null;
  status: string;
  lastSeen: string | null;
  updateStatus: 'queued' | 'pending' | 'success' | 'failed' | null;
  updateError: string | null;
  updateFromVersion: string | null;
  updateToVersion: string | null;
  lastUpdateAt: string | null;
  updateRequestedAt?: string | null;
  updateAttempts?: number;
  updateStep?: string | null;
  updateProgress?: number;
  orgId: string | null;
  isOnLatest: boolean;
  supportsUpdater?: boolean;
  queued?: boolean;
}
export interface AgentUpdatesSnapshot {
  latestVersion: string | null;
  updaterMinVersion?: string;
  releases: Array<{ version: string; channel: string; publishedAt: string; sha256: string; downloadUrl: string; fileSize: number }>;
  policy: { state: string; releaseVersion: string | null; canaryPct: number; startedAt: string; promotedAt: string | null } | null;
  summary: { total: number; onLatest: number; behind: number; failed: number; pending: number; unknown: number };
  devices: AgentUpdateRow[];
}
export interface AgentUpdateLogs {
  device: {
    id: string; hostname: string; agentVersion: string | null;
    updateStatus: string | null; updateStep: string | null; updateProgress: number;
    updateError: string | null; updateFromVersion: string | null; updateToVersion: string | null;
    lastUpdateAt: string | null; updateRequestedAt: string | null; updateAttempts: number;
    lastSeen: string | null;
  };
  events: Array<{ id: string; when: string; type: string; payload: any; severity?: string }>;
}
export const agentUpdatesApi = {
  list: (): Promise<AgentUpdatesSnapshot> => apiFetch('/api/admin/agent-updates'),
  update: (id: string) => apiFetch(`/api/admin/agent-updates/device/${id}/update`, { method: 'POST' }),
  retry:  (id: string) => apiFetch(`/api/admin/agent-updates/retry/${id}`, { method: 'POST' }),
  updateAll: (deviceIds?: string[]): Promise<{ ok: boolean; queued: number; targetVersion: string }> =>
    apiFetch('/api/admin/agent-updates/update-all', { method: 'POST', body: JSON.stringify(deviceIds ? { deviceIds } : {}) }),
  logs: (id: string): Promise<AgentUpdateLogs> => apiFetch(`/api/admin/agent-updates/logs/${id}`),
  policy: (action: 'freeze' | 'resume' | 'canary', canaryPct?: number) =>
    apiFetch(`/api/admin/agent-updates/policy/${action}`, { method: 'POST', body: JSON.stringify(canaryPct ? { canaryPct } : {}) }),
};

// Phase 22.5 — the first agent version that ships with the self-update
// Updater hosted service. Anything older (4.2.0/4.2.1/4.2.2/4.2.3) is
// MSI-install only. Re-exported here so multiple components can render
// the same warning consistently.
export const AGENT_UPDATER_MIN_VERSION = '4.2.4';

// Phase 22.6 — Legacy Agent Cleanup API. The inventory endpoint
// classifies every device into msi / ps1 / unknown and tells us which
// can self-update, which need a manual MSI reinstall, and which boxes
// are currently spamming the server with stale tokens.
export interface LegacyAgentRow {
  deviceId: string;
  hostname: string;
  os: string;
  agentVersion: string | null;
  agentType: 'msi' | 'ps1' | 'unknown';
  agentRuntime: string;
  lastUserAgent: string;
  lastSeen: string | null;
  isLegacy: boolean;
  isModern: boolean;
  isUnknown: boolean;
  isUpdateCapable: boolean;
}
export interface LegacyAgentSnapshot {
  generatedAt: string;
  counts: { total: number; legacy: number; modern: number; unknown: number; updateCapable: number };
  devices: LegacyAgentRow[];
  staleTokenSpam: {
    active: number;
    totalHits: number;
    sample: Array<{ hash: string; ip: string | null; ua: string; path: string; count: number; firstSeen: string; lastSeen: string }>;
  };
}
export interface LegacyMigratePlan {
  deviceId: string;
  hostname: string;
  currentAgentType: 'msi' | 'ps1' | 'unknown';
  currentAgentVersion: string | null;
  enrollToken: string;
  expiresAt: string;
  serverUrl: string;
  cleanup: string;
  installCmd: string;
  msiCmd: string;
  oneLiner: string;
  steps: string[];
}
export const agentLegacyApi = {
  list:    (): Promise<LegacyAgentSnapshot> => apiFetch('/api/admin/agent-legacy'),
  count:   (): Promise<{ legacy: number; unknown: number; modern: number; total: number }> =>
    apiFetch('/api/admin/agent-legacy/count'),
  migrate: (deviceId: string): Promise<LegacyMigratePlan> =>
    apiFetch(`/api/admin/agent-legacy/migrate/${deviceId}`, { method: 'POST' }),
};

// Phase 12 — Settings (org.settings blob + live system stats + device list).
export const settingsApi = {
  get:    () => apiFetch('/api/settings'),
  live:   () => apiFetch('/api/settings/live'),
  update: (settings: unknown) =>
    apiFetch('/api/settings', { method: 'PUT', body: JSON.stringify(settings) }),
};

// Phase 9 — Rules engine API.
export const rulesApi = {
  list:    (includeDisabled = false) => apiFetch(`/api/rules${includeDisabled ? '?includeDisabled=true' : ''}`),
  get:     (id: string) => apiFetch(`/api/rules/${id}`),
  create:  (body: any) => apiFetch('/api/rules', { method: 'POST', body: JSON.stringify(body) }),
  update:  (id: string, patch: any) => apiFetch(`/api/rules/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  toggle:  (id: string, enabled: boolean) => apiFetch(`/api/rules/${id}/toggle`, { method: 'POST', body: JSON.stringify({ enabled }) }),
  remove:  (id: string) => apiFetch(`/api/rules/${id}`, { method: 'DELETE' }),
  resolve: (params: { userId?: string; team?: string; department?: string }) => {
    const q = new URLSearchParams(params as any).toString();
    return apiFetch(`/api/rules/resolve${q ? '?' + q : ''}`);
  },
  classify: (kind: 'app' | 'url', value: string, ctx: { userId?: string; team?: string; department?: string } = {}) => {
    const q = new URLSearchParams({ ...(ctx as any), [kind]: value }).toString();
    return apiFetch(`/api/rules/classify?${q}`);
  },
};

// Phase 29 — Advanced Productivity Rules Engine.
export type ClassType = 'productive' | 'communication' | 'neutral' | 'unproductive';
export interface AppRuleRow { id: string | null; appName: string; displayName: string; category: string; type: ClassType; isOverride: boolean; source: 'builtin' | 'override' | 'custom'; }
export interface UrlRuleRow { id: string | null; domain: string; category: string; type: ClassType; isOverride: boolean; source: 'builtin' | 'override' | 'custom'; }
export interface ProductivityConfig {
  workStart: string; workEnd: string; idleThresholdSeconds: number;
  weights: { productive: number; communication: number; neutral: number; unproductive: number };
  alerts: Record<string, any>;
}
export interface RuleAlert { id: string; deviceId: string; hostname: string; employee: string; kind: string; severity: 'critical' | 'warning' | 'info'; title: string; message: string; detectedAt: string; }
export interface ScoreBreakdown {
  deviceId: string; date: string; hostname: string; employee: string; score: number;
  totalMinutes: number; totalFormatted: string; idleMinutes: number; idleFormatted: string;
  activeMinutes: number; weightedMinutes: number; screenshots: number;
  weights: ProductivityConfig['weights'];
  byType: Record<string, { minutes: number; weight: number; weightedMinutes: number; formatted: string }>;
}

export const productivityRulesApi = {
  apps: (): Promise<{ apps: AppRuleRow[]; categories: string[]; types: ClassType[] }> => apiFetch('/api/rules/apps'),
  saveApp: (body: { appName: string; displayName?: string; category: string; type: ClassType }) =>
    apiFetch('/api/rules/apps', { method: 'POST', body: JSON.stringify(body) }),
  bulkApps: (appNames: string[], category: string, type: ClassType) =>
    apiFetch('/api/rules/apps/bulk', { method: 'POST', body: JSON.stringify({ appNames, category, type }) }),
  deleteApp: (id: string) => apiFetch(`/api/rules/apps/${id}`, { method: 'DELETE' }),

  urls: (): Promise<{ urls: UrlRuleRow[]; categories: string[]; types: ClassType[] }> => apiFetch('/api/rules/urls'),
  saveUrl: (body: { domain: string; category: string; type: ClassType }) =>
    apiFetch('/api/rules/urls', { method: 'POST', body: JSON.stringify(body) }),
  deleteUrl: (id: string) => apiFetch(`/api/rules/urls/${id}`, { method: 'DELETE' }),

  config: (): Promise<{ config: ProductivityConfig }> => apiFetch('/api/rules/productivity'),
  saveConfig: (patch: Partial<ProductivityConfig>) =>
    apiFetch('/api/rules/productivity', { method: 'PATCH', body: JSON.stringify(patch) }),

  alerts: (deviceId?: string): Promise<{ alerts: RuleAlert[]; generatedAt: string; date: string }> =>
    apiFetch(`/api/rules/alerts${deviceId ? '?deviceId=' + deviceId : ''}`),
  score: (deviceId: string, date?: string): Promise<ScoreBreakdown> =>
    apiFetch(`/api/rules/score?deviceId=${deviceId}${date ? '&date=' + date : ''}`),
};

// Phase 9 — Org licensing API.
export const licensingApi = {
  get:        () => apiFetch('/api/licensing'),
  status:     () => apiFetch('/api/licensing/status'),
  create:     (body: any) => apiFetch('/api/licensing', { method: 'POST', body: JSON.stringify(body) }),
  update:     (patch: any) => apiFetch('/api/licensing', { method: 'PATCH', body: JSON.stringify(patch) }),
  suspend:    (reason?: string) => apiFetch('/api/licensing/suspend', { method: 'POST', body: JSON.stringify({ reason }) }),
  reactivate: () => apiFetch('/api/licensing/reactivate', { method: 'POST' }),
  renew:      (body: any) => apiFetch('/api/licensing/renew', { method: 'POST', body: JSON.stringify(body) }),
  rotateKey:  () => apiFetch('/api/licensing/rotate-key', { method: 'POST' }),
};

// Phase 9 — Auth sessions / refresh-token API.
export const authSessionsApi = {
  refresh:   (refreshToken?: string) => apiFetch('/api/auth/refresh', { method: 'POST', body: JSON.stringify({ refreshToken }) }),
  logout:    (refreshToken?: string) => apiFetch('/api/auth/logout',  { method: 'POST', body: JSON.stringify({ refreshToken }) }),
  list:      () => apiFetch('/api/auth/sessions'),
  revoke:    (id: string) => apiFetch(`/api/auth/sessions/${id}/revoke`, { method: 'POST' }),
  revokeAll: () => apiFetch('/api/auth/sessions/revoke-all', { method: 'POST' }),
};

// Phase 9 — Ops/health endpoints (unauthenticated).
// Phase 12.2 — added `ingestion` for the dashboard freshness banner.
// Phase 15 HOTFIX — call /api/health/* instead of /health/*. The
// production nginx config proxies /api/ to node but has no /health/
// block, so the bare paths fall through to the SPA index.html and
// the JSON parse throws. Server now mounts the health router at both
// paths; this client uses the /api/ alias so the call survives
// nginx without a config change. `safeJson` ensures we never crash
// the page on a transient HTML/empty/timeout response.
// Parse a health response. Throws on non-JSON / empty / parse failure
// with a specific message so react-query's isError + the OpsPage
// interpreter can show a meaningful state instead of "Cannot reach".
async function safeJson(res: Response) {
  const text = await res.text().catch(() => '');
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}${text ? ': ' + text.slice(0, 120) : ''}`);
  }
  if (!text) throw new Error('Empty response from health endpoint');
  if (text.trim().startsWith('<')) {
    // Came back as HTML — most likely a misconfigured proxy serving
    // the SPA index for this path. Surface a specific message so the
    // ops UX can guide the fix.
    throw new Error('Health endpoint returned HTML (proxy misconfiguration)');
  }
  try {
    return JSON.parse(text);
  } catch (e: any) {
    throw new Error('Health endpoint returned malformed JSON: ' + e.message);
  }
}
export const opsApi = {
  health:     () => fetch(`${API_URL}/api/health`).then(safeJson),
  ready:      () => fetch(`${API_URL}/api/health/ready`).then(safeJson),
  authHealth: () => fetch(`${API_URL}/api/health/auth`).then(safeJson),
  ops:        () => fetch(`${API_URL}/api/health/ops`).then(safeJson),
  ingestion:  () => fetch(`${API_URL}/api/health/ingestion`).then(safeJson),
};

// ─── Phase 31 — GPS tracking & geofencing ───────────────────────────────────
export interface DeviceLocation {
  deviceId: string; hostname: string; status: string;
  lat: number; lng: number; accuracy: number | null;
  capturedAt: string; lastSeen: string | null;
  activeApp: string | null; assignedUser: string | null;
  insideZones: string[]; productiveMs: number; unproductiveMs: number;
}
export interface LocationPoint { id: string; lat: number; lng: number; accuracy: number | null; capturedAt: string; source: string; }
export interface GeofenceZone {
  _id: string; name: string; lat: number; lng: number; radiusMeters: number;
  color: string; active: boolean; alertOnExit: boolean; createdAt?: string;
}
export interface GeofenceEvent {
  id: string; deviceId: string; zoneId: string; zoneName: string;
  type: 'enter' | 'exit'; lat: number | null; lng: number | null; timestamp: string;
}

export const locationApi = {
  current: (): Promise<{ devices: DeviceLocation[] }> => apiFetch('/api/locations/current'),
  history: (deviceId: string, from?: string, to?: string): Promise<{ points: LocationPoint[] }> =>
    apiFetch(`/api/locations/history?deviceId=${encodeURIComponent(deviceId)}${from ? '&from=' + encodeURIComponent(from) : ''}${to ? '&to=' + encodeURIComponent(to) : ''}`),
  add: (deviceId: string, lat: number, lng: number, accuracy?: number): Promise<{ ok: boolean; entered: string[]; exited: string[] }> =>
    apiFetch('/api/locations', { method: 'POST', body: JSON.stringify({ deviceId, lat, lng, accuracy, source: 'manual' }) }),
};

export const geofenceApi = {
  list: (): Promise<{ zones: GeofenceZone[] }> => apiFetch('/api/geofences'),
  create: (z: Partial<GeofenceZone>): Promise<{ zone: GeofenceZone }> =>
    apiFetch('/api/geofences', { method: 'POST', body: JSON.stringify(z) }),
  update: (id: string, patch: Partial<GeofenceZone>): Promise<{ zone: GeofenceZone }> =>
    apiFetch(`/api/geofences/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  remove: (id: string): Promise<{ ok: boolean }> =>
    apiFetch(`/api/geofences/${id}`, { method: 'DELETE' }),
  events: (params: { deviceId?: string; zoneId?: string; type?: string; from?: string; to?: string } = {}): Promise<{ events: GeofenceEvent[] }> => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString();
    return apiFetch(`/api/geofences/events${qs ? '?' + qs : ''}`);
  },
  alerts: (): Promise<{ alerts: RuleAlert[]; generatedAt: string }> => apiFetch('/api/geofences/alerts'),
};

// ─── Phase 33 — Notifications & Email ────────────────────────────────────────
export interface NotificationItem {
  id: string; kind: string;
  severity: 'info' | 'success' | 'warning' | 'critical';
  title: string; description: string;
  actionHref: string | null; actionLabel: string | null;
  createdAt: string; archivedAt: string | null; read: boolean; recipientId: string | null;
}
export interface NotificationSettings {
  emailEnabled: boolean; inAppEnabled: boolean;
  frequency: 'instant' | 'daily' | 'weekly' | 'off';
  types: Record<string, boolean>;
  quietHoursStart: string | null; quietHoursEnd: string | null;
  categories: string[];
}

export const notificationsApi = {
  list: (opts: { limit?: number; includeArchived?: boolean; includeRead?: boolean } = {}): Promise<{ items: NotificationItem[]; unread: number }> => {
    const qs = new URLSearchParams();
    if (opts.limit) qs.set('limit', String(opts.limit));
    if (opts.includeArchived) qs.set('includeArchived', 'true');
    if (opts.includeRead === false) qs.set('includeRead', 'false');
    return apiFetch(`/api/notifications${qs.toString() ? '?' + qs : ''}`);
  },
  unread: (): Promise<{ unread: number }> => apiFetch('/api/notifications/unread'),
  markRead: (id: string) => apiFetch(`/api/notifications/${id}/read`, { method: 'POST' }),
  markAllRead: () => apiFetch('/api/notifications/read-all', { method: 'POST' }),
  archive: (id: string) => apiFetch(`/api/notifications/${id}/archive`, { method: 'POST' }),
  remove: (id: string) => apiFetch(`/api/notifications/${id}`, { method: 'DELETE' }),
  getSettings: (): Promise<NotificationSettings> => apiFetch('/api/notifications/settings'),
  updateSettings: (patch: Partial<NotificationSettings>): Promise<NotificationSettings> =>
    apiFetch('/api/notifications/settings', { method: 'PUT', body: JSON.stringify(patch) }),
  testEmail: (): Promise<{ ok: boolean; queued: boolean; to: string }> =>
    apiFetch('/api/notifications/test-email', { method: 'POST' }),
};
